import { useFieldArray, useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import type { PortfolioFormValues } from "@/lib/portfolio-schema";

export const EMPTY_ACCOUNT = { label: "", avgCost: "", shares: "" };

/**
 * Dynamic list of account rows (label / GAV / antal aktier). Reused by the
 * create page and the dashboard edit dialog. Must be rendered inside a
 * react-hook-form <Form> whose values match {@link PortfolioFormValues}.
 */
export function AccountsFields() {
  const { control } = useFormContext<PortfolioFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "accounts" });

  return (
    <div className="space-y-3">
      {fields.map((field, index) => (
        <div key={field.id} className="flex items-end gap-2">
          <FormField
            control={control}
            name={`accounts.${index}.label`}
            render={({ field }) => (
              <FormItem className="flex-1">
                {index === 0 && <FormLabel>Konto</FormLabel>}
                <FormControl>
                  <Input placeholder="t.ex. Privatkonto" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`accounts.${index}.avgCost`}
            render={({ field }) => (
              <FormItem className="w-28">
                {index === 0 && <FormLabel>GAV</FormLabel>}
                <FormControl>
                  <Input type="number" step="any" inputMode="decimal" placeholder="0,12" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`accounts.${index}.shares`}
            render={({ field }) => (
              <FormItem className="w-36">
                {index === 0 && <FormLabel>Antal aktier</FormLabel>}
                <FormControl>
                  <Input type="number" step="any" inputMode="numeric" placeholder="1000" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mb-px text-muted-foreground hover:text-destructive"
            onClick={() => remove(index)}
            disabled={fields.length === 1}
            aria-label="Ta bort konto"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={() => append({ ...EMPTY_ACCOUNT })}>
        <Plus className="size-4" />
        Lägg till konto
      </Button>
    </div>
  );
}
